// Build submission gates — what a deal needs before anyone can submit its
// build (Oct 22, 2026).
//
// Greg: "hard gates to submit a build are signed agreement, shingle and drip
// edge color authorization from homeowner, insurance scope uploaded and first
// monies or a plan for first monies."
//
// These three pure checks are the client mirror of the database gate
// deals_gate_build_documents() (migration 20261022_build_approval_single_route).
// The fourth gate, the first payment or a plan for it, is asked at the Submit
// click (BuildPaymentPlanPicker) and enforced by deals_gate_build_payment_plan().
// ⚠ Change the SQL and this file together.

import type { Deal } from "./types";

export type SubmitGateDeal = Pick<
  Deal,
  | "install_agreement_signed"
  | "shingle_color"
  | "drip_edge_color"
  | "color_addendum_required"
  | "color_addendum_signed"
  | "insurance_paperwork_uploaded"
  | "scope_document_url"
  | "is_retail"
  | "rcv_total"
  | "retail_contract_total"
>;

const filled = (v: string | null | undefined): boolean => typeof v === "string" && v.trim().length > 0;

/** The homeowner signed the install agreement (the portal's e-sign flag, which
 *  staff may also set by hand for a paper signature). */
export function agreementSigned(d: SubmitGateDeal): boolean {
  return d.install_agreement_signed === true;
}

/** Both colors are on the deal. */
export function colorsChosen(d: SubmitGateDeal): boolean {
  return filled(d.shingle_color) && filled(d.drip_edge_color);
}

/**
 * The homeowner authorized the shingle and drip edge colors, one of two ways:
 *  - they chose both on the agreement they signed (ho_sign_agreement stores
 *    the picks; color_addendum_required stays off), or
 *  - they said "not yet" at signing and later signed the color addendum
 *    (color_addendum_signed).
 * Colors typed in by staff with neither are not an authorization.
 */
export function homeownerColorsAuthorized(d: SubmitGateDeal): boolean {
  if (!colorsChosen(d)) return false;
  if (d.color_addendum_signed === true) return true;
  if (d.color_addendum_required === true) return false;
  return d.install_agreement_signed === true;
}

/** The carrier's scope is on file. Retail jobs have none. The same three
 *  signals ScopeSection.scopeUploaded uses, minus the live deal_documents
 *  count (the database checks that one too). */
export function insuranceScopeOnFile(d: SubmitGateDeal): boolean {
  return d.is_retail === true || d.insurance_paperwork_uploaded === true || filled(d.scope_document_url);
}

/** The scope numbers are entered (retail: the contract total). */
export function scopeEntered(d: SubmitGateDeal): boolean {
  return d.is_retail === true ? Number(d.retail_contract_total ?? 0) > 0 : Number(d.rcv_total ?? 0) > 0;
}

export interface SubmitGate {
  key: "agreement_signed" | "colors_authorized" | "insurance_uploaded" | "scope_entered";
  label: string;
  met: boolean;
}

/** Every gate with its state, in the order the checklist shows them. */
export function submitGates(d: SubmitGateDeal): SubmitGate[] {
  return [
    { key: "agreement_signed", label: "Install agreement signed by the homeowner", met: agreementSigned(d) },
    { key: "colors_authorized", label: "Shingle & drip edge colors authorized by the homeowner", met: homeownerColorsAuthorized(d) },
    { key: "insurance_uploaded", label: "Insurance scope uploaded", met: insuranceScopeOnFile(d) },
    { key: "scope_entered", label: "Scope entered", met: scopeEntered(d) },
  ];
}

/** Labels of the gates still open. Empty when the build can be submitted. */
export function missingSubmitGates(d: SubmitGateDeal): string[] {
  return submitGates(d).filter((g) => !g.met).map((g) => g.label);
}
